import logger from "../../../../log";
import ReactLazyError from "../../../common/ReactLazyError";

import {
    getDefaultMultiLevelConfig,
    getDefaultMultiLevelParams,
} from "./9-multi-level/defaults";
import type { AppDefinition } from "./types";

const eFn = (e: Error) => {
    logger.error(
        `Error lazy loading app component: ${e.message}`,
        undefined,
        e,
    );
    return { default: ReactLazyError };
};

export const ValidAppIds = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
] as const;
export type AppId = (typeof ValidAppIds)[number];

// App IDs for AI-powered workouts (Third Shot Drop, Serve & Volley, AOI Triggered)
export const AI_APP_IDS: AppId[] = [8, 10, 15, 19];

const apps: Record<AppId, AppDefinition> = {
    1: {
        id: 1,
        name: "Freeplay",
        loadEditComponent: () => import("./1-freeplay/AimSpinPlay").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("./1-freeplay/AimSpinPlay").catch(eFn),
        loadCardComponent: () => import("./5-single-shot/Card").catch(eFn),
        loadHeroComponent: () => import("./5-single-shot/Card").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    2: {
        id: 2,
        name: "Responsive Play",
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadPlayComponent: () =>
            import("./2-responsive/ResponsivePlayApp").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadCardComponent: () => import("./2-responsive/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero"),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    3: {
        id: 3,
        name: "Point-and-Click Lob App",
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadPlayComponent: () => import("./3-lobs/LobApp2").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadCardComponent: () => import("./3-lobs/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    4: {
        id: 4,
        name: "Curated Workout",
        loadEditComponent: () => import("./4-multi-shot/Edit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("./4-multi-shot/PlayScreen").catch(eFn),
        loadCardComponent: () => import("./4-multi-shot/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./4-multi-shot/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    5: {
        id: 5,
        name: "Single Shot Skills",
        loadEditComponent: () =>
            import("./5-single-shot/SingleShot").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./5-single-shot/SingleShot").catch(eFn),
        loadCardComponent: () => import("./5-single-shot/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./5-single-shot/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    6: {
        id: 6,
        name: "User Created Workout",
        loadEditComponent: () => import("./4-multi-shot/Edit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("./4-multi-shot/PlayScreen").catch(eFn),
        loadCardComponent: () => import("./4-multi-shot/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./4-multi-shot/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    7: {
        id: 7,
        name: "Shared Workout",
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadCardComponent: () => import("../Shared/CardWrapper").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    8: {
        id: 8,
        name: "Pickleball Game",
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./8-pickleball-game/pickleBallGame").catch(eFn),
        loadCardComponent: () => import("../Shared/CardWrapper").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    9: {
        id: 9,
        name: "Multi-Level Workout",
        loadEditComponent: () => import("./9-multi-level/Edit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./9-multi-level/PlayScreen").catch(eFn),
        loadCardComponent: () => import("./9-multi-level/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./9-multi-level/Icon").catch(eFn),
        getDefaultConfig: getDefaultMultiLevelConfig,
        getDefaultParams: getDefaultMultiLevelParams,
    },
    10: {
        id: 10,
        name: "Serve and Volley",
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./10-serve-and-volley/serveAndVolleyNewUI").catch(eFn),
        loadCardComponent: () => import("../Shared/CardWrapper").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    11: {
        id: 11,
        name: "Module",
        loadEditComponent: () =>
            import("./11-module/ModuleEdit/ModuleEdit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("./11-module/ModulePlay").catch(eFn),
        loadCardComponent: () => import("./11-module/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./11-module/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    12: {
        id: 12,
        name: "Serve Challenge",
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadListComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadPlayComponent: () => import("./12-serve-challenge").catch(eFn),
        loadCardComponent: () => import("../Shared/CardWrapper").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    13: {
        id: 13,
        name: "Baseline Depth",
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("./13-baseline-depth").catch(eFn),
        loadCardComponent: () => import("../Shared/CardWrapper").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    14: {
        id: 14,
        name: "Serve and Volley", // V3
        loadEditComponent: () => import("../Shared/NotAvailable").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./14-serve-and-volley-v3/serveAndVolleyV4").catch(eFn),
        loadCardComponent: () => import("../Shared/CardWrapper").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadHistoryComponent: () =>
            import("./14-serve-and-volley-v3/History").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    15: {
        id: 15,
        name: "Zone Triggered",
        loadEditComponent: () => import("./15-aoi-triggered/Edit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./15-aoi-triggered/AOITriggeredApp").catch(eFn),
        loadCardComponent: () => import("../Shared/CardWrapper").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    // 16 (DUPR, pickleball) and 17 (Padel) load the SAME components. The
    // runtimes are forked on coach so a padel change during the trial cannot
    // reach a DUPR play; the play screens are not, because they were already
    // parameterized by sport — instructions, the 3D guide and the visualizer
    // targets all key off `sport`, so a second copy would be two places to fix
    // every padel wording change.
    //
    // The shared directory carries the padel name because padel is the app
    // being actively worked; DUPR loading from `17-padel-app` reads oddly and
    // is worth revisiting if DUPR outlives the trial.
    16: {
        id: 16,
        name: "DUPR App",
        loadEditComponent: () =>
            import("./17-padel-app/ModuleEdit/ModuleEdit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("./17-padel-app/ModulePlay").catch(eFn),
        loadCardComponent: () => import("./17-padel-app/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./17-padel-app/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    17: {
        id: 17,
        name: "Padel Assessment",
        loadEditComponent: () =>
            import("./17-padel-app/ModuleEdit/ModuleEdit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () => import("./17-padel-app/ModulePlay").catch(eFn),
        loadCardComponent: () => import("./17-padel-app/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./17-padel-app/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    // 18 and 19 are lacrosse forks of Module (11) and Zone Triggered (15),
    // forked on both webby-app and coach so the lacrosse MVP can change
    // without reaching the other sports' plays.
    // The lacrosse builders and play screen, not the Module ones: the generic
    // Module editor would drop `config.lacrosse` on save.
    18: {
        id: 18,
        name: "Lacrosse Module",
        loadEditComponent: () =>
            import("./18-lacrosse-module/screens/LacrosseEdit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./18-lacrosse-module/screens/LacrossePlay").catch(eFn),
        loadCardComponent: () => import("./18-lacrosse-module/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./18-lacrosse-module/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
    19: {
        id: 19,
        name: "Lacrosse Zone Triggered",
        // Zone drills: one ball each time a player runs into a zone. The
        // zone builder and play screen; they share the lacrosse builders'
        // parts and are listed with the player workouts.
        loadEditComponent: () =>
            import("./19-lacrosse-aoi-triggered/screens/ZoneEdit").catch(eFn),
        loadListComponent: () => import("../Shared/ListItemWrapper").catch(eFn),
        loadPlayComponent: () =>
            import("./19-lacrosse-aoi-triggered/screens/ZonePlay").catch(eFn),
        loadCardComponent: () => import("./18-lacrosse-module/Card").catch(eFn),
        loadHeroComponent: () => import("./shared/Hero").catch(eFn),
        loadIconComponent: () => import("./18-lacrosse-module/Icon").catch(eFn),
        getDefaultConfig: () => ({}),
        getDefaultParams: () => ({}),
    },
};

export function getApp(id: AppId): AppDefinition {
    return apps[id];
}

export function findApp(id: number): AppDefinition | null {
    return apps[id as AppId] ?? null;
}

export function getApps(): AppDefinition[] {
    return Object.values(apps);
}

export function getAppId(idMaybe: string | undefined): AppId | null {
    if (idMaybe) {
        const parsed = parseInt(idMaybe, 10);
        if (!Number.isNaN(parsed)) {
            if (ValidAppIds.includes(parsed as AppId)) {
                return parsed as AppId;
            }
        }
    }

    return null;
}
